import type { CSSProperties, ReactNode } from "react";
import { BRANDBOOK } from "@/lib/brand/brandbook";
import {
  landingHtmlHasMarkup,
  landingHtmlToPlain,
  renderLandingHtml,
  renderPlainWithBreaks,
} from "@/lib/landing/puck/landing-rich-html";

export {
  LANDING_FONT_FAMILIES,
  LANDING_TEXT_COLOR_PRESETS,
  LANDING_TEXT_COLOR_PRESETS_DARK,
  LANDING_COLOR_DARK_VAR,
  LANDING_COLOR_LIGHT_VAR,
  LANDING_SIZE_LIGHT_VAR,
  LANDING_SIZE_DARK_VAR,
  LANDING_WEIGHT_LIGHT_VAR,
  LANDING_WEIGHT_DARK_VAR,
  LANDING_STYLE_LIGHT_VAR,
  LANDING_STYLE_DARK_VAR,
  LANDING_DECO_LIGHT_VAR,
  LANDING_DECO_DARK_VAR,
  LANDING_FONT_LIGHT_VAR,
  LANDING_FONT_DARK_VAR,
  LANDING_RICH_COLOR_CLASS,
  LANDING_RICH_THEME_CLASS,
  LANDING_THEME_STYLE_VARS,
} from "@/lib/landing/puck/landing-rich-html";

export const LANDING_TEXT_ALIGNS = ["left", "center", "right"] as const;
export type LandingTextAlign = (typeof LANDING_TEXT_ALIGNS)[number];

/** Текст поля блока: строка (старые страницы) или объект со стилем / инлайн-HTML. */
export type LandingStyledText = {
  text: string;
  /** Инлайн-разметка: span/br/b/i/u/s. Источник правды, если есть теги. */
  html?: string;
  fontSizePx?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  /** Выравнивание блока целиком (не фрагмента). */
  align?: LandingTextAlign;
};

export type LandingTextValue = string | LandingStyledText;

export const LANDING_FONT_SIZE_PX = [
  12, 14, 16, 18, 20, 22, 24, 28, 30, 32, 36, 40, 48, 56, 64,
] as const;

export const LANDING_BG_COLOR_PRESETS: { label: string; value: string }[] = [
  { label: "Как тема", value: "" },
  { label: "Белый", value: BRANDBOOK.white },
  { label: "Нейтральный", value: BRANDBOOK.mist },
  { label: "Розовый", value: BRANDBOOK.blush },
  { label: "Тёмный", value: BRANDBOOK.ink },
  { label: "Акцент", value: BRANDBOOK.rose },
];

export function isLandingStyledText(value: unknown): value is LandingStyledText {
  return Boolean(
    value &&
      typeof value === "object" &&
      "text" in value &&
      typeof (value as { text: unknown }).text === "string",
  );
}

export function parseLandingTextAlign(value: unknown): LandingTextAlign | undefined {
  if (value === "left" || value === "center" || value === "right") return value;
  return undefined;
}

export function landingTextAlignClass(
  value: unknown,
  fallback?: LandingTextAlign,
): string {
  const align = resolveStyledText(value).align ?? fallback;
  if (align === "left") return "text-left";
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return "";
}

/** Полная ширина + text-align, чтобы выравнивание не съедалось таблицей и Heading. */
export function landingAlignWrap(
  value: unknown,
  fallback?: LandingTextAlign,
): { className: string; style: CSSProperties } {
  const align = resolveStyledText(value).align ?? fallback;
  return {
    className: `block w-full min-w-0 ${landingTextAlignClass(value, fallback)}`.trim(),
    style: align ? { textAlign: align } : {},
  };
}

export function resolveStyledText(value: unknown): LandingStyledText {
  if (typeof value === "string") return { text: value };
  if (isLandingStyledText(value)) {
    const fontSizePx =
      typeof value.fontSizePx === "number" &&
      Number.isFinite(value.fontSizePx) &&
      value.fontSizePx > 0
        ? Math.round(value.fontSizePx)
        : undefined;
    const html =
      typeof value.html === "string" && value.html.trim()
        ? value.html
        : undefined;
    const text = html ? landingHtmlToPlain(html) || value.text : value.text;
    const align = parseLandingTextAlign(value.align);
    return {
      text,
      ...(html ? { html } : {}),
      ...(fontSizePx ? { fontSizePx } : {}),
      ...(value.bold ? { bold: true } : {}),
      ...(value.italic ? { italic: true } : {}),
      ...(value.underline ? { underline: true } : {}),
      ...(value.strike ? { strike: true } : {}),
      ...(align ? { align } : {}),
    };
  }
  return { text: "" };
}

export function styledTextString(value: unknown): string {
  const parsed = resolveStyledText(value);
  if (parsed.html) return landingHtmlToPlain(parsed.html) || parsed.text;
  return parsed.text;
}

export function landingTextHasInlineMarkup(value: unknown): boolean {
  if (!isLandingStyledText(value)) return false;
  return Boolean(value.html && landingHtmlHasMarkup(value.html));
}

export function asLandingTextValue(value: unknown): LandingTextValue {
  if (typeof value === "string") return value;
  if (isLandingStyledText(value)) return value;
  return "";
}

export function compactStyledText(
  value: LandingStyledText,
): LandingTextValue {
  const parsed = resolveStyledText(value);
  const html =
    parsed.html && landingHtmlHasMarkup(parsed.html) ? parsed.html : undefined;
  if (
    !html &&
    !parsed.fontSizePx &&
    !parsed.bold &&
    !parsed.italic &&
    !parsed.underline &&
    !parsed.strike &&
    !parsed.align
  ) {
    return parsed.text;
  }
  return {
    text: parsed.text,
    ...(html ? { html } : {}),
    ...(parsed.fontSizePx ? { fontSizePx: parsed.fontSizePx } : {}),
    ...(parsed.bold ? { bold: true } : {}),
    ...(parsed.italic ? { italic: true } : {}),
    ...(parsed.underline ? { underline: true } : {}),
    ...(parsed.strike ? { strike: true } : {}),
    ...(parsed.align ? { align: parsed.align } : {}),
  };
}

export function styledTextStyle(value: unknown): CSSProperties | undefined {
  const parsed = resolveStyledText(value);
  const style: CSSProperties = {};
  if (parsed.fontSizePx) style.fontSize = `${parsed.fontSizePx}px`;
  if (parsed.bold) style.fontWeight = 700;
  if (parsed.italic) style.fontStyle = "italic";
  const decorations: string[] = [];
  if (parsed.underline) decorations.push("underline");
  if (parsed.strike) decorations.push("line-through");
  if (decorations.length) style.textDecoration = decorations.join(" ");
  if (parsed.align) style.textAlign = parsed.align;
  return Object.keys(style).length ? style : undefined;
}

export function landingTextChildren(value: unknown): ReactNode {
  const parsed = resolveStyledText(value);
  if (parsed.html && landingHtmlHasMarkup(parsed.html)) {
    return renderLandingHtml(parsed.html);
  }
  return renderPlainWithBreaks(parsed.text);
}

/** children + style для Heading / Text. children может содержать <br> и span. */
export function landingTextProps(value: unknown): {
  children: ReactNode;
  style?: CSSProperties;
} {
  const parsed = resolveStyledText(value);
  const inline = parsed.html ? undefined : styledTextStyle(value);
  const style: CSSProperties = { ...inline };
  if (parsed.align) style.textAlign = parsed.align;
  return {
    children: landingTextChildren(value),
    ...(Object.keys(style).length ? { style } : {}),
  };
}

export function normalizeBackgroundColor(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) {
    return trimmed;
  }
  if (/^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*[\d.]+)?\s*\)$/i.test(
    trimmed,
  )) {
    return trimmed;
  }
  return "";
}
